/**
 * Copyright IBM Corp. 2022, 2026
 */

import { vercelSplit } from '@web/cms'
import {
	hasHashiCorpDestination,
	parseUrl,
} from '@hashicorp/react-components/src/lib/parse-url'
import {
	STANDALONE_LINK_COLORS,
	STANDALONE_LINK_ICON_POSITIONS,
	STANDALONE_LINK_SIZES,
} from '@hashicorp/mds-react/components'
import type {
	StandaloneLinkColor,
	StandaloneLinkIconPosition,
	StandaloneLinkSize,
} from '@hashicorp/mds-react/components'
import type { StandaloneLinkFieldsFragment } from 'types/dato'

/**
 * Format standaloneLinkFields fragment from DatoCMS to props for @hashicorp/react-design-systems-components StandaloneLink component
 */
export default function formatStandaloneLinkToProps({
	href,
	color,
	size,
	iconPosition,
	icon,
	locale,
	...rest
}: StandaloneLinkFieldsFragment & { locale?: string }) {
	const parsedUrl = parseUrl(vercelSplit(href))
	return {
		...rest,
		href: parsedUrl.href,
		color: isValidColor(color) ? vercelSplit(color) : undefined,
		size: isValidSize(size) ? vercelSplit(size) : undefined,
		iconPosition: isValidIconPosition(iconPosition)
			? vercelSplit(iconPosition)
			: undefined,
		icon: icon && icon.length ? vercelSplit(icon) : undefined,
		isHrefExternal: !hasHashiCorpDestination(parsedUrl.href),
	}
}

function isValidColor(colorVal?: string): colorVal is StandaloneLinkColor {
	return (
		!!colorVal &&
		STANDALONE_LINK_COLORS.includes(
			vercelSplit(colorVal) as StandaloneLinkColor,
		)
	)
}

function isValidSize(sizeVal?: string): sizeVal is StandaloneLinkSize {
	return (
		!!sizeVal &&
		STANDALONE_LINK_SIZES.includes(vercelSplit(sizeVal) as StandaloneLinkSize)
	)
}

function isValidIconPosition(
	iconPositionVal?: string,
): iconPositionVal is StandaloneLinkIconPosition {
	return (
		!!iconPositionVal &&
		STANDALONE_LINK_ICON_POSITIONS.includes(
			vercelSplit(iconPositionVal) as StandaloneLinkIconPosition,
		)
	)
}
